import { Check, Hourglass } from "lucide-react";

export interface TaskCheckProps {
  state: "todo" | "working" | "done" | "held";
  /**
   * Makes the check a real completion affordance (praxis-intent-relay
   * §3.7): rendered as a `<button>` ONLY for `todo`/`working` with a
   * handler. Other states — and the zero-extra-props form the mockup
   * uses — render exactly the pre-write-path glyphs.
   */
  onComplete?: () => void;
  /**
   * A relayed complete intent is in flight. Renders the sky pulsing
   * ring with `aria-busy` — NEVER the done check (the projection could
   * contradict it) and never the terracotta working spinner.
   */
  pending?: boolean;
  /** Accessible label context for the interactive form. */
  taskTitle?: string;
}

/** The self-completing checkbox — the whole point: the agent fills it in. */
export function TaskCheck({ state, onComplete, pending, taskTitle }: TaskCheckProps) {
  if (pending === true && (state === "todo" || state === "working")) {
    return (
      <span
        aria-busy="true"
        className="h-[22px] w-[22px] shrink-0 animate-pulse rounded-full border-2 border-home-sky ring-2 ring-home-sky/30 dark:border-home-sky dark:ring-home-sky/40"
      />
    );
  }
  if (state === "done") {
    return (
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-accent-green/20">
        <Check className="h-3.5 w-3.5 text-accent-green" strokeWidth={3} />
      </span>
    );
  }
  if (state === "working") {
    if (onComplete !== undefined) {
      return (
        <button
          type="button"
          onClick={onComplete}
          aria-label={taskTitle !== undefined ? `Mark "${taskTitle}" done` : "Mark done"}
          className="group flex h-[22px] w-[22px] shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-home-terracotta/40 transition-colors hover:border-accent-green dark:border-home-terracotta/50 dark:hover:border-accent-green"
        >
          <Check
            className="h-3.5 w-3.5 text-accent-green opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            strokeWidth={3}
          />
        </button>
      );
    }
    return (
      <span className="h-[22px] w-[22px] shrink-0 animate-spin rounded-full border-2 border-home-terracotta/25 border-t-home-terracotta" />
    );
  }
  if (state === "held") {
    return (
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-home-butter">
        <Hourglass className="h-3 w-3 text-home-ink" strokeWidth={2.4} />
      </span>
    );
  }
  if (onComplete !== undefined) {
    return (
      <button
        type="button"
        onClick={onComplete}
        aria-label={taskTitle !== undefined ? `Mark "${taskTitle}" done` : "Mark done"}
        className="group flex h-[22px] w-[22px] shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-home-ink/20 transition-colors hover:border-accent-green dark:border-white/20 dark:hover:border-accent-green"
      >
        <Check
          className="h-3.5 w-3.5 text-accent-green opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          strokeWidth={3}
        />
      </button>
    );
  }
  return (
    <span className="h-[22px] w-[22px] shrink-0 rounded-full border-2 border-home-ink/20 dark:border-white/20" />
  );
}
